<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useClipboard } from "@vueuse/core"
import { useFetchWrapper } from "@core/composables/useFetchWrapper"
import { coreServiceRegistry } from "@core/services/register"

// ================================
// INTERNATIONALIZATION
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            title: "Demande d'accès aux données créée",
            help: "Vous pouvez copier le lien ci-dessous pour l'envoyer directement au titulaire.",
            notif: "Le titulaire recevra un email avec ce lien pour donner son consentement.",
            success: "L'email a bien été envoyé.",
            see: "Voir l'aperçu",
            copy: "Copier le lien",
            send: "Envoyer l'email",
            quit: "Terminer",
            missingRequestId: "Identifiant de demande manquant.",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================

const props = withDefaults(
    defineProps<{
        /** Identifiant unique de la demande multi-compteurs */
        requestId: string
        /** Lien de la demande (copie pour le titulaire) */
        link?: string
    }>(),
    {
        link: "",
    }
)

const emit = defineEmits<{
    /**
     * Après envoi réussi : l’utilisateur a cliqué sur « Terminer » pour passer à la signature
     * (équivalent machine `SENT`).
     */
    sent: []
    /** Fermer le parcours sans avoir envoyé, ou depuis l’extérieur du flux succès */
    "click:quit": []
}>()

// ================================
// SERVICES
// ================================

const injectedServices = inject<{
    consentRequest?: typeof coreServiceRegistry.consentRequest
}>("services")
const consentRequestService =
    injectedServices?.consentRequest ?? coreServiceRegistry.consentRequest

// ================================
// COMPOSABLES
// ================================

const { copy } = useClipboard()

// ================================
// STATE
// ================================

/** Prévisualisation HTML du mail (après clic « Voir l'aperçu ») */
const previewHtml = ref<string | null>(null)
/** Message de succès affiché après envoi réussi */
const successMessage = ref("")
/** Indique que le lien a été copié (feedback visuel) */
const copyDone = ref(false)

// ================================
// FETCH WRAPPERS
// ================================

/**
 * Envoi de l'e-mail d'invitation pour une demande multi-compteurs.
 */
async function sendInvitationFn(): Promise<void> {
    await consentRequestService.sendConsentRequestMulti(props.requestId, false)
    successMessage.value = t("success")
}

const {
    fetch: fetchSend,
    isFetching: isSending,
    hasError: hasSendError,
    error: sendError,
} = useFetchWrapper(sendInvitationFn)

/**
 * Prévisualisation du mail d'invitation multi.
 */
async function previewInvitationFn(): Promise<void> {
    const html = await consentRequestService.previewConsentRequestMulti(
        props.requestId
    )
    previewHtml.value = html
}

const {
    fetch: fetchPreview,
    isFetching: isPreviewing,
    hasError: hasPreviewError,
    error: previewError,
} = useFetchWrapper(previewInvitationFn)

// ================================
// WATCHERS
// ================================

watch([hasSendError, hasPreviewError], () => {
    if (hasSendError.value || hasPreviewError.value) {
        previewHtml.value = null
    }
})

// ================================
// COMPUTED
// ================================

/** Message d'erreur à afficher (envoi ou prévisualisation) */
const getErrorMessage = computed(() => {
    const err = sendError.value || previewError.value
    if (!err) return ""
    const withResponse = err as Error & {
        response?: {
            data?: {
                message?: string
                detail?: { message?: string }
            }
            status?: number
        }
    }
    const data = withResponse?.response?.data
    return (
        data?.detail?.message ??
        data?.message ??
        err.message ??
        "Une erreur est survenue."
    )
})

/** Indique s'il y a une erreur API */
const hasError = computed(() => hasSendError.value || hasPreviewError.value)

/** Lien à copier */
const getCopyLink = computed(() => props.link ?? "")

/** Boutons d'envoi / aperçu actifs */
const getHasRequestId = computed(
    () => typeof props.requestId === "string" && props.requestId.trim() !== ""
)

// ================================
// INTERACTIONS
// ================================

/**
 * Terminer : si l’e-mail a été envoyé avec succès, passage à l’étape signature ;
 * sinon fermeture du parcours (équivalent ancien « Fermer »).
 */
function onQuit(): void {
    emit("click:quit")
}

function onPreview(): void {
    fetchPreview()
}

function onSend(): void {
    fetchSend()
}

/**
 * Copie le lien d'invitation dans le presse-papier et affiche un feedback.
 */
async function onClickCopy(): Promise<void> {
    if (!getCopyLink.value) return
    await copy(getCopyLink.value)
    copyDone.value = true
    setTimeout(() => {
        copyDone.value = false
    }, 1000)
}
</script>

<template>
    <div
        class="source-form-multi-send-invitation flex flex-col items-center gap-4 w-full"
    >
        <!-- ================================ -->
        <!-- EN-TÊTE -->
        <!-- ================================ -->
        <header class="w-full flex flex-col gap-2 text-center">
            <h2
                class="text-lg font-semibold text-gray-800 flex items-center justify-center gap-2"
            >
                {{ t("title") }}
                <BaseIcon icon="pi pi-check" class="text-green-600" />
            </h2>
            <p class="text-sm text-gray-600">
                {{ t("help") }}
            </p>
            <p class="text-sm text-gray-600">
                {{ t("notif") }}
            </p>
        </header>

        <!-- ================================ -->
        <!-- ACTIONS : APERÇU, COPIER, ENVOYER -->
        <!-- ================================ -->
        <div
            v-if="!successMessage && getHasRequestId"
            class="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-4 items-center justify-items-center py-6 w-full"
        >
            <BaseButton
                :label="t('see')"
                severity="secondary"
                :loading="isPreviewing"
                :disabled="isPreviewing"
                class="w-full sm:justify-self-end"
                @click="onPreview"
            />
            <BaseButton
                :label="t('copy')"
                outlined
                :disabled="!getCopyLink"
                class="w-full sm:w-auto gap-2"
                @click="onClickCopy"
            >
                <BaseIcon
                    :icon="copyDone ? 'pi pi-check' : 'pi pi-copy'"
                    :class="copyDone ? 'text-green-600' : ''"
                />
            </BaseButton>
            <BaseButton
                :label="t('send')"
                :loading="isSending"
                :disabled="isSending"
                class="w-full sm:justify-self-start"
                @click="onSend"
            />
        </div>

        <!-- ================================ -->
        <!-- AVERTISSEMENT SANS ID REQUEST -->
        <!-- ================================ -->
        <p
            v-if="!getHasRequestId"
            class="text-sm text-center text-amber-700 py-4"
        >
            {{ t("missingRequestId") }}
        </p>

        <!-- ================================ -->
        <!-- MESSAGE DE SUCCÈS (après envoi e-mail) -->
        <!-- ================================ -->
        <BaseMessage
            v-if="successMessage"
            severity="success"
            :closable="false"
            class="w-fit mx-auto"
        >
            {{ successMessage }}
        </BaseMessage>

        <!-- ================================ -->
        <!-- MESSAGE D'ERREUR -->
        <!-- ================================ -->
        <BaseMessage
            v-if="hasError && getErrorMessage"
            severity="error"
            :closable="false"
            class="w-fit mx-auto"
        >
            {{ getErrorMessage }}
        </BaseMessage>

        <!-- ================================ -->
        <!-- IFRAME APERÇU MAIL -->
        <!-- ================================ -->
        <div
            v-if="previewHtml"
            class="w-full relative rounded border border-gray-200 overflow-hidden"
        >
            <iframe
                :srcdoc="previewHtml"
                title="Aperçu du mail"
                class="w-full border-0 min-h-[200px] pointer-events-none"
                scrolling="no"
                @load="
                    (e: Event) => {
                        const iframe = e.target as HTMLIFrameElement
                        if (iframe?.contentWindow?.document?.body) {
                            iframe.style.height =
                                iframe.contentWindow.document.body
                                    .scrollHeight + 'px'
                        }
                    }
                "
            />
        </div>

        <!-- ================================ -->
        <!-- TERMINER (suite signature ou fermeture) -->
        <!-- ================================ -->
        <div class="w-full flex justify-center">
            <BaseButton
                :label="t('quit')"
                class="max-w-[15rem] w-full"
                severity="secondary"
                @click="onQuit"
            />
        </div>
    </div>
</template>
